/**
 * Zustand store for transcript data (isolated from React Context).
 *
 * Why Zustand instead of React Context?
 * React Context re-renders ALL consumers when the value changes (by reference).
 * IntroPanel inside the R3F Canvas consumes VoiceStateContext for buttons/state,
 * but does NOT need transcript data. If transcript data were in VoiceStateContext,
 * every 500ms progressive transcription update would trigger IntroPanel re-renders,
 * causing uikit layout thrashing that starves the R3F frame loop → canvas freeze.
 *
 * Zustand uses selector-based subscriptions: components only re-render when their
 * selected slice changes. TranscriptOverlay (HTML, outside Canvas) subscribes to
 * transcript data. Nothing inside the Canvas subscribes → no re-renders.
 */

import { create } from 'zustand'

export interface TranscriptState {
  transcript: { user: string; assistant: string }
  interimTranscript: string
  transcriptStatus: 'idle' | 'interim' | 'final'

  // Actions
  setTranscript: (updater: { user: string; assistant: string } | ((prev: { user: string; assistant: string }) => { user: string; assistant: string })) => void
  setInterimTranscript: (text: string) => void
  setTranscriptStatus: (status: 'idle' | 'interim' | 'final') => void
  resetTranscript: () => void
}

export const useTranscriptStore = create<TranscriptState>((set) => ({
  transcript: { user: '', assistant: '' },
  interimTranscript: '',
  transcriptStatus: 'idle',

  setTranscript: (updater) =>
    set((state) => ({
      transcript: typeof updater === 'function' ? updater(state.transcript) : updater,
    })),

  setInterimTranscript: (text) => set({ interimTranscript: text }),

  setTranscriptStatus: (status) => set({ transcriptStatus: status }),

  resetTranscript: () =>
    set({
      transcript: { user: '', assistant: '' },
      interimTranscript: '',
      transcriptStatus: 'idle',
    }),
}))
